import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import CustomTable from "../../components/customTable/CustomTable";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../../Hooks/AdminContext";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import { Icon } from "@iconify/react";
import StatusChip from "../../theme/chip";
import CustomDrawer from "../../components/CustomDrawer";
import FilteredChip from "../../components/FilteredChip";
import AccountFilter from "./AgencyAdAccountFilter";
import { formatNumber, show_proper_words } from "../../utils/constant";
import CustomCircularProgress from "../../components/CustomCircularProgress";
import { _List_Subscription_Transactions_For_Admin } from "../../DAL/Listing/TransactionList";
import moment from "moment";

const TransactionsList = () => {
  const { setnavbarTitle } = useAdminContext();
  const { enqueueSnackbar } = useSnackbar();
  const [filterDrawerState, setFilterDrawerState] = useState(false);
  const EMPTY_FILTER = {
    account_type: "",
    status_value: "",
  };
  const [filterData, setFilterData] = useState(EMPTY_FILTER);
  const [filterStateUpdated, setFilterStateUpdated] = useState(EMPTY_FILTER);
  const [loading, setLoading] = useState(true);
  const [userList, setUserList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(userList.length);
  const [pageCount, setPageCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const handleOpenFilterDrawer = () => {
    setFilterDrawerState(true);
  };

  const handleCloseFilterDrawer = () => {
    setFilterDrawerState(false);
  };

  const handleChangeOthers = (name, value) => {
    setFilterData((values) => ({ ...values, [name]: value }));
  };

  // This function handles page changes for pagination.
  // It updates the page state to the new page number and adjusts the page count accordingly.
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage <= 0) {
      setPageCount(1);
    } else {
      setPageCount(newPage + 1);
    }
  };

  // This function handles changes in the number of rows per page for pagination.
  // It updates the rowsPerPage state to the new value and resets the page state to 0.
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // This function handles the page change for pagination.
  // If the new page is less than or equal to 0, it resets the page to 0 and sets the page count to 1.
  // Otherwise, it updates the page state to the new page and adjusts the page count accordingly.
  const handleChangePages = (event, newPage) => {
    if (newPage <= 0) {
      setPage(0);
      setPageCount(1);
    } else {
      setPage(newPage - 1);
      setPageCount(newPage);
    }
  };

  const User_Table_Head = [
    {
      id: "number",
      label: "#",
      type: "number",
    },
    {
      id: "user",
      label: "User",
      renderData: (row) => (
        <div
        // className="pointer"
        // onClick={() => {
        //   setOpenModal(true);
        // }}
        >
          <div className=" capitalized">
            {row?.user_info?.first_name + " " + row?.user_info?.last_name ||
              "_ _"}
          </div>
          <div className="text-muted" style={{ fontSize: "0.9em" }}>
            {row?.user_info?.email || "_ _"}
          </div>
        </div>
      ),
    },
    {
      id: "product_type",
      label: "Product Type",
      renderData: (row) => (
        <div className=" capitalized">
          {row?.product_info?.product_type || "_ _"}
        </div>
      ),
    },
    {
      id: "amount",
      label: "Amount",
      renderData: (row) => <div>${formatNumber(row?.amount) || "$0"}</div>,
    },
    {
      id: "transaction_date",
      label: "Transaction Date",
      renderData: (row) => (
        <div>{moment(row.date_time).format("DD MMM YY hh:mm A") || "_ _"}</div>
      ),
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <StatusChip
          status={show_proper_words(row.status)}
          type="activeInactive"
        />
      ),
    },
  ];

  /**
   * getTransactionsList - Fetches a list of subscription transactions for the admin.
   *
   * This function sends a request to the _List_Subscription_Transactions_For_Admin API
   * with filter data and pagination parameters. It updates the state with the transaction
   * list, total count, and total pages. It also handles loading state and error notifications.
   */
  const getTransactionsList = async (page, rowsPerPage, filterData, search) => {
    const PostData = { ...filterData };
    PostData.search = search;
    const formData = {
      account_type: filterData.account_type,
      status: filterData.status_value,
    };
    setLoading(true);
    const response = await _List_Subscription_Transactions_For_Admin(
      formData,
      page,
      rowsPerPage
    );
    if (response.code === 200) {
      const { total_count, total_pages, transaction = [] } = response;
      let chipData = { ...filterData };
      if (chipData.status_value) {
        chipData.status_value = show_proper_words(chipData.status_value);
      }

      setFilterStateUpdated(chipData);
      setTotalCount(total_count);
      setTotalPages(total_pages);
      setUserList(transaction);
      setLoading(false);
    } else {
      setLoading(false);
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  /**
   * searchFunction - Executes a search for subscription transactions based on the current filter data.
   *
   * This function resets the pagination, stores the current filter data in localStorage,
   * and fetches the transaction list using the getTransactionsList function. It also closes
   * the filter drawer after the search is initiated.
   */
  const searchFunction = () => {
    setPage(0);
    setTotalPages(1);
    localStorage.setItem(
      "Subscriptions_Trasactions_Trasactions_List_Filter",
      JSON.stringify(filterData)
    );
    getTransactionsList(0, rowsPerPage, filterData, searchText);
    handleCloseFilterDrawer();
  };

  /**
   * handleClearFilter - Clears the filter data and resets pagination for subscription transactions.
   *
   * This function removes the saved filter data from localStorage, resets the filter state to an empty state,
   * and fetches the transaction list with default filter values. It also closes the filter drawer after clearing.
   */
  const handleClearFilter = () => {
    localStorage.removeItem(
      "Subscriptions_Trasactions_Trasactions_List_Filter"
    );
    setFilterData(EMPTY_FILTER);
    setPage(0);
    setPageCount(1);
    getTransactionsList(0, rowsPerPage, EMPTY_FILTER, searchText);
    handleCloseFilterDrawer();
  };

  /**
   * HandleDeleteChip - Removes a filter chip and updates the transaction list based on the modified filter.
   *
   * This function checks for missing filter values, resets the filter data, and updates the transaction list
   * accordingly. It also updates the filter data in both the state and localStorage for persistence.
   */
  const HandleDeleteChip = (data) => {
    if (!data.account_type) {
      data.account_type = "";
    }
    if (!data.status_value) {
      data.status_value = "";
    }
    setPage(0);
    getTransactionsList(0, rowsPerPage, data, searchText);
    setFilterData(data);
    localStorage.setItem(
      "Subscriptions_Trasactions_Trasactions_List_Filter",
      JSON.stringify(data)
    );
  };

  /**
   * useEffect - Initializes the transaction list and filter state based on stored data.
   *
   * This hook sets the page title, retrieves the saved filter and search text from localStorage,
   * and updates the transaction list accordingly. It also updates the filter state if previously saved filters exist.
   * When the page or rowsPerPage changes, the transaction list is re-fetched with the new filters.
   */
  useEffect(() => {
    setnavbarTitle("Transactions Lists");
    const StoredFilter =
      JSON.parse(
        localStorage.getItem(
          "Subscriptions_Trasactions_Trasactions_List_Filter"
        )
      ) || {};
    const search = localStorage.getItem(
      "Subscriptions_Trasactions_Trasactions_List_search_text"
    );
    if (Object.keys(StoredFilter).length > 0) {
      setFilterData(StoredFilter);
      setFilterStateUpdated(StoredFilter);
    }
    const finalFilter =
      Object.keys(StoredFilter).length > 0 ? StoredFilter : filterData;
    getTransactionsList(page, rowsPerPage, finalFilter, search || "");
    if (search) setSearchText(search);
  }, [page, rowsPerPage]);

  if (loading) {
    return <CustomCircularProgress />;
  }

  return (
    <div className="mt-4">
      <div className="row mb-3">
        <div className="col-12 col-md-10">
          <FilteredChip
            data={filterStateUpdated}
            tempState={filterData}
            EMPTY_FILTER={EMPTY_FILTER}
            onDeleteChip={HandleDeleteChip}
            onClear={handleClearFilter}
          />
        </div>
        <div className="col-12 col-md-2 text-end">
          <Button variant="contained" onClick={handleOpenFilterDrawer}>
            Filters &nbsp;&nbsp; <Icon icon={roundFilterList} />
          </Button>
        </div>
      </div>
      <CustomTable
        data={userList}
        TABLE_HEAD={User_Table_Head}
        MENU_OPTIONS={[]}
        custom_pagination={{
          total_count: totalCount,
          rows_per_page: rowsPerPage,
          page: page,
          handleChangePage: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
        }}
        pageCount={pageCount}
        totalPages={totalPages}
        handleChangePages={handleChangePages}
        pagePagination={true}
        is_hide={true}
      />
      <CustomDrawer
        isOpenDrawer={filterDrawerState}
        onOpenDrawer={handleOpenFilterDrawer}
        onCloseDrawer={() => setFilterDrawerState(false)}
        pageTitle="Filters"
        componentToPassDown={
          <AccountFilter
            deposit={true}
            filterData={filterData}
            handleClearFilter={handleClearFilter}
            handleChangeOthers={handleChangeOthers}
            searchSubmitFilter={searchFunction}
            setFilterData={setFilterData}
          />
        }
      />
    </div>
  );
};

export default TransactionsList;
